import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '../../components/Button';
import { Flex } from '../../components/Flex';
import { Input } from '../../components/Input';
import { PaidFeatureNotice } from '../../components/PaidFeatureNotice';
import { Text } from '../../components/Text';
import { Modal } from '../../components/modal/Modal';
import { urlTestIDs } from '../../constants/test/url.testIds';
import { AtiraLinkPlanType } from '../../model/plan/types/AtiraLinkPlanType.enum';
import { Lengths } from '../../model/shared/enum/Lengths.enum';
import { AtiraURL } from '../../model/url/AtiraURL';
import { UpdateURLDto } from '../../model/url/dto/UpdateURLDto';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { shortenUrlActions } from '../../redux/url/url.slice';
import { userSliceSelectors } from '../../redux/user/user.selector';
import { Spacing } from '../../theme/Spacing';
import { AtiraToast } from '../../utils/AtiraToast';
import { onSetTypedCRUDErrors } from '../../utils/onSetTypedCRUDErrors';

const Container = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: ${Spacing.m};
`;

const StyledButton = styled(Button)`
  height: 2.3rem;
  width: 8rem;

  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
  }
`;

type ShortURLUpdateModalProps = {
  open: boolean;
  onClose: VoidFunction;
  url: AtiraURL | null;
};

export const ShortURLUpdateModal: React.FC<ShortURLUpdateModalProps> = ({
  open = false,
  onClose,
  url,
}) => {
  const { t } = useTranslation();

  const loggedInUser = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  const { setError, getValues, control, reset, handleSubmit } =
    useForm<UpdateURLDto>();

  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();

  const userPlan =
    loggedInUser?.plan?.freeTrial?.freeTrialPlan ||
    loggedInUser?.plan?.unpaidPlan?.unpaidPlanType ||
    loggedInUser.plan?.type;

  const onLocalClose = () => {
    reset();
    onClose();
  };

  const onShortenURL = async () => {
    try {
      setLoading(true);
      const dto = getValues();

      await dispatch(shortenUrlActions.updateURL(dto)).unwrap();
      await dispatch(
        shortenUrlActions.getUserShortURLs({ userId: loggedInUser._id }),
      ).unwrap();

      AtiraToast.success(t('shortURL.update.success'));
      reset();
      onLocalClose();
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (url && open) {
      reset({
        urlId: url._id,
        originalURL: url.originalURL,
        label: url.label,
        userId: loggedInUser._id,
        customIdentifier: url.isCustom ? url.identifier : undefined,
      });
    }
  }, [url, open, reset, loggedInUser._id]);

  if (!url) {
    return null;
  }

  return (
    <Modal title={t('shortURL.modal.title')} open={open} onClose={onLocalClose}>
      <Container>
        <Text color="fgSecondary">
          {t('shortURL.modal.subtitle', { url: url.shortURL })}
        </Text>
        <Controller
          control={control}
          name="originalURL"
          rules={{
            required: {
              value: true,
              message: t('common.this_field_required'),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              title={t('shorten_url.input.label')}
              placeholder="https://example.com"
              value={value}
              onChange={onChange}
              valid={!error}
              errorMessage={error?.message}
              type="url"
              height="2.5rem"
              required
              info={t('shortenURL.input.info')}
            />
          )}
        />

        <Controller
          control={control}
          name="label"
          rules={{
            maxLength: {
              value: Lengths.DESCRIPTION,
              message: t('error.length.less', {
                name: t('common.label'),
                length: Lengths.DESCRIPTION,
              }),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              title={t('common.label')}
              placeholder={t('shortenURL.label.placeholder')}
              value={value}
              onChange={onChange}
              valid={!error}
              errorMessage={error?.message}
              height="2.5rem"
              info={t('shortenURL.label.info')}
              maxLength={Lengths.DESCRIPTION}
            />
          )}
        />

        <Controller
          control={control}
          name="customIdentifier"
          rules={{
            pattern: {
              value: /^[a-zA-Z0-9]{6}$/,
              message: t('shortenURL.customIdentifier.pattern.error'),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              title={t('shortenURL.customIdentifier')}
              placeholder={'abc123'}
              info={t('shortenURL.customIdentifier.info')}
              value={value}
              onChange={onChange}
              valid={!error}
              errorMessage={error?.message}
              dataTestId={urlTestIDs.shorten.customIdentifier}
              type="text"
              height="2.5rem"
              disabled={userPlan !== AtiraLinkPlanType.PLAN_2}
              maxLength={6}
            />
          )}
        />

        {userPlan !== AtiraLinkPlanType.PLAN_2 ? (
          <PaidFeatureNotice plan={AtiraLinkPlanType.PLAN_2} />
        ) : null}

        <StyledButton onClick={handleSubmit(onShortenURL)} loading={loading}>
          {t('common.update')}
        </StyledButton>
      </Container>
    </Modal>
  );
};
